/**
 * EmptyState Component
 * Reusable component to display when no data is available
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { LucideIcon } from 'lucide-react';

// ============================================
// Props Interface
// ============================================

interface EmptyStateProps {
  Icon: LucideIcon;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}

// ============================================
// EmptyState Component
// ============================================

export const EmptyState: React.FC<EmptyStateProps> = ({
  Icon,
  title,
  description,
  actionLabel,
  onAction,
}) => {
  return (
    <Box className="flex min-h-[400px] items-center justify-center">
      <Box className="text-center">
        {/* Icon */}
        <Box className="mb-4 flex justify-center">
          <Box className="rounded-full bg-slate-100 p-6 dark:bg-slate-800">
            <Icon size={48} className="text-slate-400 dark:text-slate-500" />
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          className="mb-2 font-semibold text-gray-900 dark:text-slate-100"
        >
          {title}
        </Typography>

        {/* Description */}
        {description && (
          <Typography variant="body2" className="mb-6 text-gray-600 dark:text-slate-400">
            {description}
          </Typography>
        )}

        {/* Action Button */}
        {actionLabel && onAction && (
          <Button
            variant="contained"
            onClick={onAction}
            className="bg-blue-500 hover:bg-blue-600"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};
